import { EAccessLevel } from "../../../../../shared/oversight-core/enums/access-level";
import { IAccessGrant } from "../../../../../shared/oversight-core/interfaces/access-grant";
import AppSelect, {
  Option,
} from "../../../../../shared/oversight-core/ui-elements/app-select/app-select";
import MaterialIcon from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import {
  accessScopeLabels,
  getAccessLevelInfo,
  grantableAccessLevels,
  isExpiryRequired,
  isGrantExpired,
} from "../../../../../shared/oversight-core/utils/get-org-user-info";
import GrantExpiryPicker from "../../grant-expiry-picker/grant-expiry-picker";
import styles from "./grant-row.module.scss";

interface IProps {
  grant: IAccessGrant;
  // Levels the user's role may hold; a Monitoring guest gets View redacted only.
  allowedLevels: EAccessLevel[];
  onChange: (grant: IAccessGrant) => void;
  onRemove: () => void;
}

const GrantRow = (props: IProps) => {
  const { grant, allowedLevels, onChange, onRemove } = props;
  const isExpired = !grant.isMissing && isGrantExpired(grant);
  const isLevelNotAllowed =
    !grant.isMissing && !allowedLevels.includes(grant.level);

  // A level the role no longer allows stays listed so the row still shows what it holds.
  const levelOptions: Option[] = grantableAccessLevels
    .filter((level) => allowedLevels.includes(level) || level === grant.level)
    .map((level) => ({
      value: level,
      label: getAccessLevelInfo(level).label,
      isDisabled: !allowedLevels.includes(level),
    }));

  const getRowClass = () => {
    if (grant.isMissing) return styles.missing;
    if (isExpired) return styles.expired;
    return "";
  };

  const getTag = () => {
    if (grant.isMissing) return "Not found";
    if (isExpired) return "Expired";
    return accessScopeLabels[grant.scope];
  };

  return (
    <div className={`${styles.grantRow} ${getRowClass()}`}>
      <div className={styles.target}>
        <div className={`${styles.path} font-size-11`}>
          {grant.isMissing
            ? "Site no longer exists"
            : grant.path.join(" › ") || accessScopeLabels[grant.scope]}
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
          <span className={`${styles.name} font-size-14 font-weight-600`}>
            {grant.scopeName}
          </span>
          <span className={`${styles.tag} font-size-11 font-weight-500`}>
            {getTag()}
          </span>
        </div>
        {isLevelNotAllowed && (
          <div className="text-danger font-size-11 mt-1">
            A Monitoring guest can only hold View redacted.
          </div>
        )}
      </div>
      <div className="d-flex align-items-center gap-2">
        <AppSelect
          className={`${styles.levelSelect} ${
            grant.isMissing ? styles.levelSelectDanger : ""
          }`}
          options={levelOptions}
          selectedValue={levelOptions.find((o) => o.value === grant.level)}
          onChangeOption={(option) =>
            onChange({ ...grant, level: option.value as EAccessLevel })
          }
          disabled={grant.isMissing}
          isOptionsDisabled={(option) => option.isDisabled}
          fontSize="13px"
        />
        <GrantExpiryPicker
          expiresAt={grant.expiresAt}
          onChange={(expiresAt) => onChange({ ...grant, expiresAt })}
          variant={
            grant.isMissing ? "danger" : isExpired ? "warning" : "default"
          }
          disabled={grant.isMissing}
          allowNever={!isExpiryRequired(grant.level)}
        />
        <MaterialIcon
          icon="delete"
          size={20}
          color={grant.isMissing ? "#e45e5e" : "#69768b"}
          className="cursor-pointer"
          onClick={onRemove}
        />
      </div>
    </div>
  );
};

export default GrantRow;
